// « Arrivées » — the arrivals sheet for the desk (PMS-TERMINAL-LINK-BRIEF
// §8.8g, §8.8k).
//
// The departures sheet's twin (operator 2026-09-25): the day's arrivals in two
// sections — not yet arrived on top, arrived below — each with its stay, the
// arrhes already taken and every payment (date, method, amount), since how a
// guest paid is part of the checks.
//
// Since §8.8k (operator 2026-09-26) it is also where the day's payments are
// taken, like Départs: each arrival can open the Encaissement form on its room
// — the résa for a booking of several rooms — with what is left of the stay.
// A payment taken in the form carries a red ⚠ until the feed reads it in Vega,
// which the desk still keys by hand; the sheet re-reads every minute, so the
// mark clears on its own. Below the two sections: the day's OTHER form
// payments (a guest mid-stay, on neither sheet) with « Régler » / « Annuler »
// for an open « en attente », and the day's total — what the retired Journal
// listed.
//
// §8.8o (operator 2026-09-26): the money is split by DAY. « Arrhes » is
// everything paid before the sheet's day, « Réglé le JJ/MM » everything paid
// on it — Vega's lines and the form's alike, so a form payment stays in its
// column when Vega picks it up and only its ⚠ goes (the retired « Encaissé »
// column made it jump). GEODIS paid four rooms by card at check-in: that is
// the day's money, not arrhes. A past day reads as it stood that evening — a
// payment made after it belongs to a later day's sheet — while « Encaisser »
// still prefills what is left NOW. Each payment sits under the total it makes
// up, not in the guest's cell, and never wraps. Never the arrhes Vega still
// HOLDS, which the evening's note preparation consumes (arrivalToCollectCents).
//
// On top, the page's running totals by tender (DayTakings): what the arriving
// guests paid that day — the check-in shift (operator 2026-09-27).
//
// « Arrived » is Vega's own status letter (A in house / P departed, against C
// confirmed / R reserved); an arrived booking shows its REAL check-in time,
// the others the expected one Vega holds.
//
// Interim source: the Vega mirror. At go-live the adapter changes, not this
// screen.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FindingRow } from './CardFindingsContext.js';
import type { ReactNode } from 'react';
import type {
  ArrivalBill,
  ArrivalBriefing,
  DayPayment,
  IsoDate,
  OtherTaking,
  ReceptionPaymentId,
  TakingsBriefing,
} from '@lemontaigu/pms-core';
import { fmtAmount, fmtDate } from './logic.js';
import {
  arrivalOnDay,
  arrivalTarget,
  arrivalToCollectCents,
  dayMonth,
  hhmm,
  isResaTarget,
  nightsBetween,
  otherDayPayments,
  othersTakings,
  pageTakings,
  sortArrivals,
  staysOn,
  clientTag,
  timeParis,
  todayParis,
  vegaReadLabel,
  type ArrivalSort,
  type SuspenseToSettle,
} from './departureSheet.js';
import { NotInVega, PaymentList } from './PaymentList.js';
import { OtherTakings } from './OtherTakings.js';
import { DayBar } from './DayBar.js';
import { DayTakings, type LitBucket } from './DayTakings.js';
import { RoomList } from './RoomList.js';
import { SheetTop } from './SheetTop.js';
import { SortChips, type SortOption } from './SortChips.js';
import { SuspenseActions } from './SuspenseActions.js';
import { SHEET_REFRESH_MS, useAutoRefresh } from './useAutoRefresh.js';
import { EtablissementTag } from './EtablissementTag.js';

const SORTS: SortOption<ArrivalSort>[] = [
  { key: 'name', label: 'Nom' },
  { key: 'room', label: 'Chambre' },
  { key: 'time', label: 'Heure d’arrivée' },
];

export interface ArrivalsProps {
  briefing: ArrivalBriefing;
  /** The day's « Autres encaissements » (§8.8p); none without it. */
  takings?: TakingsBriefing;
  /** The view chips, placed at the top by the Encaissement screen (§8.8k). */
  nav?: ReactNode;
  /** The Encaissement form, placed under the sheet's title. */
  form?: ReactNode;
  /** Open the Encaissement form on this room or résa, with this amount. */
  onBill?: (target: string, amountCents: number) => void;
  canBill?: boolean;
  /** « Régler » / « Annuler » of an open « en attente » among the day's other payments. */
  onSettle?: (s: SuspenseToSettle) => void;
  onCancelSuspense?: (id: ReceptionPaymentId) => void;
  /** Bumped by the form after each payment it records — the sheet re-reads. */
  refreshKey?: number;
  /** How often the sheet re-reads itself while on screen; 0 turns it off. */
  refreshMs?: number;
  /** Injected for tests; the wall clock otherwise. */
  now?: Date;
}

interface ArrivalTableProps {
  rows: ArrivalBill[];
  arrived: boolean;
  empty: string;
  /** The sheet's day — what is « before » and « on » it. */
  day: IsoDate;
  today: IsoDate;
  canBill: boolean;
  onBill?: (target: string, amountCents: number) => void;
  onSettle?: (s: SuspenseToSettle) => void;
  onCancelSuspense?: (id: ReceptionPaymentId) => void;
}

function ArrivalTable({ rows, arrived, empty, day, today, canBill, onBill, onSettle, onCancelSuspense }: ArrivalTableProps) {
  const total = rows.reduce(
    (t, a) => {
      const m = arrivalOnDay(a, day);
      return {
        stay: t.stay + a.stayCents,
        before: t.before + m.beforeCents,
        onDay: t.onDay + m.onDayCents,
        pending: t.pending + a.pendingCents,
        due: t.due + Math.max(m.dueCents, 0),
      };
    },
    { stay: 0, before: 0, onDay: 0, pending: 0, due: 0 },
  );
  return (
    <div className="pms-table-wrap">
      <table className="pms-table" aria-label={arrived ? 'Arrivés' : 'Pas encore arrivés'}>
        <thead>
          <tr>
            <th>Chambre(s)</th>
            <th>Client</th>
            <th>Séjour</th>
            <th>Montant séjour</th>
            <th title={`Réglé avant le ${dayMonth(day)}`}>Arrhes</th>
            <th>Réglé le {dayMonth(day)}</th>
            <th>En attente</th>
            <th>À encaisser</th>
            <th>{arrived ? 'Arrivé à' : 'Heure prévue'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => {
            const n = nightsBetween(a.arrivalDate, a.departureDate);
            const time = hhmm(a.arrivalTime);
            const m = arrivalOnDay(a, day);
            const due = m.dueCents;
            // « Encaisser » prefills what is left NOW, whatever day is shown.
            const dueNow = arrivalToCollectCents(a);
            const target = arrivalTarget(a);
            return (
              <Fragment key={a.bookingRef}>
              <tr>
                <td>
                  <RoomList rooms={a.rooms} />
                </td>
                <td>
                  {a.partyLabel}
                  <span className="pms-sub"> {clientTag(a.clientRef, a.bookingRef)}</span>
                  <EtablissementTag code={a.propertyCode} />
                </td>
                <td>
                  <span className="pms-nowrap">
                    {dayMonth(a.arrivalDate)} → {dayMonth(a.departureDate)}
                  </span>
                  <span className="pms-sub">
                    {' '}
                    · {n} nuit{n > 1 ? 's' : ''}
                  </span>
                </td>
                <td className="pms-num">{fmtAmount(a.stayCents)}</td>
                <td className="pms-num">
                  {m.before.length ? fmtAmount(m.beforeCents) : '—'}
                  {a.depositUnattributedCents !== 0 && (
                    <span
                      className="pms-sub"
                      title="Arrhes d'un client ayant plusieurs réservations : à vérifier dans Vega"
                    >
                      {' '}
                      + {fmtAmount(a.depositUnattributedCents)} à vérifier
                    </span>
                  )}
                  <PaymentList payments={m.before} day={day} older="date" total={m.beforeCents} />
                </td>
                <td className="pms-num">
                  {m.onDay.length ? fmtAmount(m.onDayCents) : '—'}
                  <PaymentList payments={m.onDay} day={day} total={m.onDayCents} />
                </td>
                <td className="pms-num">
                  {a.pendingCents ? fmtAmount(a.pendingCents) : '—'}
                  <SuspenseActions
                    items={a.suspenses.map((s) => ({ id: s.id, cents: s.cents, target: s.roomNumber ?? a.bookingRef }))}
                    canBill={canBill}
                    onSettle={onSettle}
                    onCancel={onCancelSuspense}
                  />
                </td>
                <td className="pms-num">
                  {a.stayCents === 0 ? (
                    '—'
                  ) : due > 0 ? (
                    <strong>{fmtAmount(due)}</strong>
                  ) : due === 0 ? (
                    'soldé'
                  ) : (
                    <span
                      className="pms-sub"
                      title="Déjà payé plus que le séjour prévu par la facturation auto : vérifier dans Vega"
                    >
                      payé &gt; séjour de {fmtAmount(-due)} · vérifier
                    </span>
                  )}
                </td>
                <td>
                  {time ? (arrived ? time : `vers ${time}`) : '—'}
                  {/* Like Départs: only where something is left to collect —
                      any other payment is typed in the form above. Under the
                      time, not in a column of its own, so it never falls off
                      the page (operator 2026-09-27). */}
                  {canBill && onBill && dueNow > 0 && staysOn(a, today) && (
                    <div className="pms-cell-action pms-no-print">
                      <button
                        type="button"
                        className="pms-mini"
                        onClick={() => onBill(target, dueNow)}
                        aria-label={
                          isResaTarget(target) ? `Encaisser la résa ${target}` : `Encaisser la chambre ${target}`
                        }
                      >
                        Encaisser
                      </button>
                    </div>
                  )}
                </td>
              </tr>
              <FindingRow clientRef={a.clientRef} bookingRef={a.bookingRef} colSpan={9} />
              </Fragment>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={9} className="pms-empty">
                {empty}
              </td>
            </tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={3} className="pms-sub">
                {rows.length} réservation{rows.length > 1 ? 's' : ''}
              </td>
              <td className="pms-num">{fmtAmount(total.stay)}</td>
              <td className="pms-num">{fmtAmount(total.before)}</td>
              <td className="pms-num">{fmtAmount(total.onDay)}</td>
              <td className="pms-num">{fmtAmount(total.pending)}</td>
              <td className="pms-num">{fmtAmount(total.due)}</td>
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/** An « en attente » still open for a guest on neither sheet (§8.8k), with
 *  « Régler » / « Annuler » — the money of such guests is in « Autres
 *  encaissements » since §8.8p. Shown only when there is one. */
function OpenAttentes({
  rows,
  canBill,
  onSettle,
  onCancelSuspense,
}: {
  rows: DayPayment[];
  canBill: boolean;
  onSettle?: (s: SuspenseToSettle) => void;
  onCancelSuspense?: (id: ReceptionPaymentId) => void;
}) {
  const open = otherDayPayments(rows).filter((p) => p.tenderType === 'suspense');
  if (open.length === 0) return null;
  return (
    <>
      <div className="pms-section-bar">
        <h3 className="pms-section-title">
          En attente — autres clients <span className="pms-sub">({open.length})</span>
        </h3>
      </div>
      <div className="pms-table-wrap">
        <table className="pms-table" aria-label="En attente — autres clients">
          <thead>
            <tr>
              <th>Heure</th>
              <th>Chambre</th>
              <th>Client</th>
              <th>Montant</th>
              <th className="pms-no-print" />
            </tr>
          </thead>
          <tbody>
            {open.map((p) => (
              <tr key={p.id}>
                <td>{timeParis(p.capturedAt)}</td>
                <td>{p.roomNumber ?? '—'}</td>
                <td>
                  {p.partyLabel ?? '—'}
                  {p.bookingRef && <span className="pms-sub"> · résa {p.bookingRef}</span>}
                </td>
                <td className="pms-num">{fmtAmount(p.cents)}</td>
                <td className="pms-actions pms-no-print">
                  <SuspenseActions
                    items={[{ id: p.id, cents: p.cents, target: p.roomNumber ?? p.bookingRef ?? '' }]}
                    canBill={canBill}
                    onSettle={onSettle}
                    onCancel={onCancelSuspense}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function Arrivals({
  briefing,
  takings,
  nav,
  form,
  onBill,
  canBill = false,
  onSettle,
  onCancelSuspense,
  refreshKey = 0,
  refreshMs = SHEET_REFRESH_MS,
  now,
}: ArrivalsProps) {
  const [date, setDate] = useState<IsoDate>(() => todayParis(now ?? new Date()));
  const [rows, setRows] = useState<ArrivalBill[]>([]);
  const [day, setDay] = useState<DayPayment[]>([]);
  const [others, setOthers] = useState<OtherTaking[]>([]);
  const [lit, setLit] = useState<LitBucket>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only the latest read may land: a slow answer for yesterday must not
  // overwrite today's, and the minute tick races the date bar.
  const latest = useRef(0);

  const clock = now ?? new Date();
  const today = todayParis(clock);

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++latest.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      const [r, d, o] = await Promise.all([
        briefing.arrivals(date),
        briefing.dayPayments(date),
        takings ? takings.dayOthers(date) : Promise.resolve(null),
      ]);
      if (mine !== latest.current) return;
      setLoading(false);
      if (o?.ok) setOthers(o.value.filter((x) => x.shift === 'checkin'));
      else if (!quiet) setOthers([]);
      if (!r.ok) {
        setError(r.error.message ?? 'lecture impossible');
        if (!quiet) setRows([]);
        return;
      }
      setError(d.ok ? null : (d.error.message ?? 'lecture des encaissements impossible'));
      setRows(r.value);
      if (d.ok) setDay(d.value);
      else if (!quiet) setDay([]);
    },
    [briefing, takings, date],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // After the form records a payment: re-read quietly (no « Chargement… »).
  const seenKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === seenKey.current) return;
    seenKey.current = refreshKey;
    void load(true);
  }, [refreshKey, load]);

  const quietLoad = useCallback(() => void load(true), [load]);
  useAutoRefresh(quietLoad, refreshMs);

  // Operator 2026-09-25: by name while waiting, by the time they came once in.
  const [waitingSort, setWaitingSort] = useState<ArrivalSort>('name');
  const [arrivedSort, setArrivedSort] = useState<ArrivalSort>('time');
  const expected = useMemo(() => sortArrivals(rows.filter((a) => !a.arrived), waitingSort), [rows, waitingSort]);
  const arrived = useMemo(() => sortArrivals(rows.filter((a) => a.arrived), arrivedSort, true), [rows, arrivedSort]);
  const readAt = rows.find((a) => a.readAt)?.readAt ?? null;
  // The same instant as the departures sheet's (§8.8h): when the feed last
  // confirmed the mirror against Vega.
  const read = vegaReadLabel(readAt, clock);
  const dm = dayMonth(date);
  // The check-in shift's takings: what the day's arrivals paid that day, and
  // its « Autres » (§8.8p).
  const till = useMemo(
    () => [...pageTakings(rows.flatMap((a) => arrivalOnDay(a, date).onDay)), ...othersTakings(others, 'checkin')],
    [rows, others, date],
  );

  return (
    <section className="pms-print" aria-label="Arrivées" data-lit={lit ?? undefined}>
      <SheetTop nav={nav} onRefresh={() => void load()} canPrint={rows.length > 0} />
      <DayBar value={date} onChange={setDate} today={today} label="Date d’arrivée" />
      <div className="pms-head">
        <div>
          <h2>Arrivées du {fmtDate(date)}</h2>
          <p className="pms-hint">
            {read && (
              <span className={read.stale ? 'pms-stale' : undefined}>
                {read.stale ? '⚠ ' : ''}
                {read.text}
                {' · '}
              </span>
            )}
            Réservations Vega · arrhes : réglé avant le {dm} · réglé le {dm} : Vega et formulaire · à encaisser : le
            séjour moins les deux, tel qu’au soir du {dm} · <NotInVega /> pris au formulaire, pas encore saisi dans
            Vega · en gras : règlement hors carte.
          </p>
        </div>
      </div>

      {date <= today && <DayTakings title={`Encaissé aux arrivées du ${dm}`} lines={till} lit={lit} onLight={setLit} />}
      {form && <div className="pms-no-print pms-sheet-form">{form}</div>}
      {error && <p className="pms-warn">⚠ {error}</p>}
      {loading && <p className="pms-hint pms-no-print">Chargement…</p>}

      <div className="pms-section-bar">
        <h3 className="pms-section-title">
          Pas encore arrivés <span className="pms-sub">({expected.length})</span>
        </h3>
        <SortChips section="Pas encore arrivés" value={waitingSort} options={SORTS} onChange={setWaitingSort} />
      </div>
      <ArrivalTable
        rows={expected}
        arrived={false}
        empty={rows.length === 0 && !loading && !error ? 'Aucune arrivée ce jour-là.' : 'Tout le monde est arrivé.'}
        day={date}
        today={today}
        canBill={canBill}
        onBill={onBill}
        onSettle={onSettle}
        onCancelSuspense={onCancelSuspense}
      />

      <div className="pms-section-bar">
        <h3 className="pms-section-title">
          Arrivés <span className="pms-sub">({arrived.length})</span>
        </h3>
        <SortChips section="Arrivés" value={arrivedSort} options={SORTS} onChange={setArrivedSort} />
      </div>
      <ArrivalTable
        rows={arrived}
        arrived
        empty="Personne n’est encore arrivé."
        day={date}
        today={today}
        canBill={canBill}
        onBill={onBill}
        onSettle={onSettle}
        onCancelSuspense={onCancelSuspense}
      />

      {takings && (
        <>
          <div className="pms-section-bar">
            <h3 className="pms-section-title">
              Autres encaissements du jour <span className="pms-sub">({others.length}) · service arrivées</span>
            </h3>
          </div>
          <p className="pms-hint">
            Clients ni arrivés ni partis ce jour-là : pris ici au formulaire, ou saisis dans Vega seulement.
          </p>
          <OtherTakings rows={others} day={date} label="Autres encaissements du jour" />
        </>
      )}
      <OpenAttentes rows={day} canBill={canBill} onSettle={onSettle} onCancelSuspense={onCancelSuspense} />
    </section>
  );
}
